import { useCallback, useEffect, useMemo, useState } from 'react'

import { AddressPoint } from '../api/useAddressPointApi'
import { CreateAddressPointInput, useCreateAddressPointApi } from '../api/useCreateAddressPointApi'
import { useDeleteAddressPointApi } from '../api/useDeleteAddressPointApi'
import { UpdateAddressPointInput, useUpdateAddressPointApi } from '../api/useUpdateAddressPointApi'
import { UpdateUserInput, useUpdateUserApi } from '../api/useUpdateUserApi'
import {
  UpdateUserPasswordInput,
  useUpdateUserPasswordsApi,
} from '../api/useUpdateUserPasswordsApi'
import { Order, User, useUserApi } from '../api/useUserApi'
import { useUserOrdersApi } from '../api/useUserOrdersApi'

const initUser: User = {
  clubName: '',
  countryCode: '',
  email: '',
  firstName: '',
  id: '',
  lastName: '',
  phoneNumber: '',
  role: 'normal',
  smsTrackingOptin: false,
  smsTrackingTermsAccepted: false,
}

export interface UseUser {
  addresses: AddressPoint[]
  addressesError: Error | null
  createAddress: (input: CreateAddressPointInput) => Promise<boolean>
  currentAddressContact: (address: AddressPoint) => string
  deleteAddress: (id: string) => Promise<void>
  error: Error | null
  favoriteAddress: AddressPoint | null
  hasMoreOrders: boolean
  isCreatingAddress: boolean
  isDeletingAddress: boolean
  isLoading: boolean
  isLoadingMoreOrders: boolean
  isPro: boolean
  loadMoreOrders: () => void
  orders: Order[]
  isUpdatingAddress: boolean
  isUpdatingPassword: boolean
  passwordError: Error | null
  update: (input: UpdateUserInput) => Promise<void>
  updateAddress: (input: UpdateAddressPointInput) => Promise<void>
  updatePassword: (input: UpdateUserPasswordInput) => Promise<boolean>
  user: Omit<User, 'addresses'>
}

export const useUser = (): UseUser => {
  const [addresses, setAddresses] = useState<AddressPoint[]>([])
  const [addressesError, setAddressesError] = useState<Error | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [passwordError, setPasswordError] = useState<Error | null>(null)
  const [user, setUser] = useState<User>(initUser)

  const {
    mutateAsync: createAddressPoint,
    error: createAddressPointError,
    isPending: isCreatingAddress,
  } = useCreateAddressPointApi()

  const {
    mutateAsync: deleteAddressPoint,
    error: deleteAddressPointError,
    isPending: isDeletingAddress,
  } = useDeleteAddressPointApi()

  const { data, error: loadError, isLoading } = useUserApi()

  const {
    data: ordersData,
    fetchNextPage: fetchNextOrdersPage,
    hasNextPage: hasMoreOrders,
    isFetchingNextPage: isLoadingMoreOrders,
  } = useUserOrdersApi()

  const {
    mutateAsync: updateAddressPoint,
    error: updateAddressPointError,
    isPending: isUpdatingAddress,
  } = useUpdateAddressPointApi()

  const { mutateAsync, error: updateError, isPending } = useUpdateUserApi()

  const {
    mutateAsync: mutatePassword,
    error: updatePasswordError,
    isPending: isUpdatingPassword,
  } = useUpdateUserPasswordsApi()

  const favoriteAddress = useMemo(
    () => addresses.find(({ favorite }) => favorite) || null,
    [addresses],
  )

  const isPro = useMemo(() => user?.role === 'pro', [user?.role])

  const orders = useMemo(() => ordersData?.pages.flatMap(({ nodes }) => nodes) || [], [ordersData])

  const loadMoreOrders = useCallback(() => {
    fetchNextOrdersPage()
  }, [fetchNextOrdersPage])

  const createAddress = useCallback(
    (input: CreateAddressPointInput) =>
      createAddressPoint(input).then(({ message, success, addressPoint }) => {
        if (success && addressPoint) {
          setAddresses((prev) => [...prev, addressPoint])
          setAddressesError(null)
          return true
        }
        setAddressesError(new Error(message))
        return false
      }),
    [createAddressPoint],
  )

  const currentAddressContact = useCallback(
    ({ attentionName, firstName, lastName }: AddressPoint) =>
      attentionName || `${firstName || ''} ${lastName || ''}`.trim() || '',
    [],
  )

  const deleteAddress = useCallback(
    (id: string) =>
      deleteAddressPoint(id).then(({ message, success }) =>
        success
          ? setAddresses((prev) => prev.filter((addr) => addr.id !== id))
          : setAddressesError(new Error(message)),
      ),
    [deleteAddressPoint],
  )

  const setAddressAndUser = useCallback((user: User) => {
    setAddresses(user.addresses || [])
    setUser({ ...user, addresses: undefined })
  }, [])

  const update = useCallback(
    (input: UpdateUserInput) =>
      mutateAsync(input).then(({ message, success, user }) =>
        success ? setAddressAndUser(user) : setError(new Error(message)),
      ),
    [mutateAsync, setAddressAndUser],
  )

  const updateAddress = useCallback(
    (input: UpdateAddressPointInput) =>
      updateAddressPoint(input).then(({ message, success, addressPoint }) =>
        success
          ? setAddresses((prev) =>
              prev.map((addr) => (addr.id === addressPoint?.id ? addressPoint : addr)),
            )
          : setAddressesError(new Error(message)),
      ),
    [updateAddressPoint],
  )

  const updatePassword = useCallback(
    (input: UpdateUserPasswordInput) =>
      mutatePassword(input)
        .then(({ error, success }) => (success ? true : (setPasswordError(error), false)))
        .catch((error) => (setPasswordError(error.message), false)),
    [mutatePassword],
  )

  useEffect(() => setAddressesError(createAddressPointError || null), [createAddressPointError])
  useEffect(() => setAddressesError(deleteAddressPointError || null), [deleteAddressPointError])
  useEffect(() => setAddressesError(updateAddressPointError || null), [updateAddressPointError])
  useEffect(() => setError(loadError || updateError || null), [loadError, updateError])
  useEffect(() => setPasswordError(updatePasswordError || null), [updatePasswordError])
  useEffect(() => setAddressAndUser(data || initUser), [data, setAddressAndUser])

  return {
    addresses,
    addressesError,
    createAddress,
    currentAddressContact,
    deleteAddress,
    error,
    favoriteAddress,
    hasMoreOrders,
    isCreatingAddress,
    isDeletingAddress,
    isLoading: data === undefined || isLoading || isPending,
    isLoadingMoreOrders,
    isPro,
    loadMoreOrders,
    orders,
    isUpdatingAddress,
    isUpdatingPassword,
    passwordError,
    update,
    updateAddress,
    updatePassword,
    user,
  }
}
